<script setup lang="ts">
import { ref, computed } from 'vue'
import type { ComboScore } from '../api/client'

const props = defineProps<{
  allScores: ComboScore[]
  scoreType: string
}>()

const paramOptions = [
  { key: 'zWindow', label: 'Z Window' },
  { key: 'stepAsideThresholdBps', label: 'SELL threshold (bps)' },
  { key: 'stepAsideBuyThresholdBps', label: 'BUY threshold (bps)' },
  { key: 'tiltBps', label: 'Tilt (bps)' },
  { key: 'vixScale', label: 'VIX Scale' },
  { key: 'termStructure', label: 'Term Structure' },
  { key: 'nfpFilter', label: 'NFP Filter' },
  { key: 'holidayFilter', label: 'Holiday Filter' },
  { key: 'opexFilter', label: 'OpEx Filter' },
  { key: 'quadWitchingFilter', label: 'Quad Witching' },
  { key: 'exDividendFilter', label: 'Ex-Dividend' },
  { key: 'futuresRollFilter', label: 'Futures Roll' },
  { key: 'eomInterestFilter', label: 'EoM Interest' },
  { key: 'shortTermMr', label: 'Short-Term MR' },
]

// 2026-10-05: a Japan threshold sweep varies only the step-aside band — default to those axes there,
// otherwise the generic VIX-scale x tilt view (constant across that sweep) collapses to one cell.
function distinct(key: string): number {
  return new Set(props.allScores.map(s => String((s.params as any)?.[key]))).size
}
const bandOnly = distinct('stepAsideThresholdBps') > 1 && distinct('vixScale') <= 1 && distinct('tiltBps') <= 1
const xParam = ref(bandOnly ? 'stepAsideThresholdBps' : 'vixScale')
const yParam = ref(bandOnly ? 'stepAsideBuyThresholdBps' : 'tiltBps')

function getParamValue(params: any, key: string): string {
  const v = params[key]
  if (typeof v === 'boolean') return v ? 'Y' : 'N'
  return String(v)
}

const heatmapData = computed(() => {
  const xKey = xParam.value
  const yKey = yParam.value
  const groups = new Map<string, { sum: number; count: number }>()
  const xVals = new Set<string>()
  const yVals = new Set<string>()

  for (const s of props.allScores) {
    if (s.fitness <= -1e10) continue // skip rejected
    const xv = getParamValue(s.params, xKey)
    const yv = getParamValue(s.params, yKey)
    xVals.add(xv)
    yVals.add(yv)
    const key = `${xv}|${yv}`
    const g = groups.get(key) ?? { sum: 0, count: 0 }
    g.sum += s.fitness
    g.count++
    groups.set(key, g)
  }

  const cells = new Map<string, number>()
  let min = Infinity, max = -Infinity
  for (const [key, g] of groups) {
    const mean = g.sum / g.count
    cells.set(key, mean)
    if (mean < min) min = mean
    if (mean > max) max = mean
  }

  const xSorted = [...xVals].sort((a, b) => {
    const na = Number(a), nb = Number(b)
    if (!isNaN(na) && !isNaN(nb)) return na - nb
    return a.localeCompare(b)
  })
  const ySorted = [...yVals].sort((a, b) => {
    const na = Number(a), nb = Number(b)
    if (!isNaN(na) && !isNaN(nb)) return na - nb
    return a.localeCompare(b)
  })

  return { cells, xSorted, ySorted, min, max }
})

function cellColor(val: number): string {
  const { min, max } = heatmapData.value
  if (max === min) return '#fbc02d'
  const t = (val - min) / (max - min) // 0 to 1
  // Interpolate red → yellow → green
  if (t < 0.5) {
    const r = 211, g = Math.round(47 + (192 - 47) * t * 2), b = Math.round(47 + (45 - 47) * t * 2)
    return `rgb(${r},${g},${b})`
  } else {
    const r = Math.round(251 - (251 - 56) * (t - 0.5) * 2), g = Math.round(192 - (192 - 142) * (t - 0.5) * 2), b = Math.round(45 - (45 - 60) * (t - 0.5) * 2)
    return `rgb(${r},${g},${b})`
  }
}

function textColor(val: number): string {
  const { min, max } = heatmapData.value
  const t = max === min ? 0.5 : (val - min) / (max - min)
  return t < 0.3 || t > 0.7 ? '#fff' : '#111'
}
</script>

<template>
  <div class="heatmap-wrap">
    <div class="section-label">Parameter Sensitivity</div>
    <div class="axis-selectors">
      <label>X: <select v-model="xParam">
        <option v-for="o in paramOptions" :key="o.key" :value="o.key">{{ o.label }}</option>
      </select></label>
      <label>Y: <select v-model="yParam">
        <option v-for="o in paramOptions" :key="o.key" :value="o.key">{{ o.label }}</option>
      </select></label>
    </div>
    <table class="heatmap-table" v-if="heatmapData.xSorted.length && heatmapData.ySorted.length">
      <thead>
        <tr>
          <th></th>
          <th v-for="x in heatmapData.xSorted" :key="x">{{ x }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="y in heatmapData.ySorted" :key="y">
          <th>{{ y }}</th>
          <td
            v-for="x in heatmapData.xSorted"
            :key="x"
            :style="{
              background: heatmapData.cells.has(`${x}|${y}`) ? cellColor(heatmapData.cells.get(`${x}|${y}`)!) : '#111',
              color: heatmapData.cells.has(`${x}|${y}`) ? textColor(heatmapData.cells.get(`${x}|${y}`)!) : '#666',
            }"
          >{{ heatmapData.cells.has(`${x}|${y}`) ? heatmapData.cells.get(`${x}|${y}`)!.toFixed(3) : '--' }}</td>
        </tr>
      </tbody>
    </table>
    <div v-else class="no-data">No heatmap data available</div>
  </div>
</template>

<style scoped>
.heatmap-wrap { margin-bottom: 16px; }
.section-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 1px; padding-bottom: 4px; border-bottom: 1px solid #222; margin-bottom: 8px; }
.axis-selectors { display: flex; gap: 16px; margin-bottom: 8px; font-size: 12px; color: #aaa; }
.axis-selectors select { background: #1a1a1a; color: #ccc; border: 1px solid #333; padding: 3px 6px; border-radius: 3px; font-size: 11px; }
.heatmap-table { border-collapse: collapse; font-size: 11px; font-family: monospace; }
.heatmap-table th { padding: 4px 8px; color: #888; font-weight: 500; font-size: 10px; text-align: center; }
.heatmap-table td { padding: 6px 10px; text-align: center; border: 1px solid #222; min-width: 60px; font-size: 11px; }
.no-data { color: #666; font-size: 12px; padding: 12px 0; }
</style>
